<template>
    <div>
        <verify-phone ref="verifyPhonePopup" @verified="refreshVerifiedEvents"></verify-phone>
        <b-container class="content" fluid="lg">
            <spinner-view v-if="!viewModel.isLoaded"></spinner-view>
            <b-row v-if="viewModel.isLoaded" class="no-gutters">
                <b-col class="pt-5">
                    <!-- <navigation-view></navigation-view> -->
                </b-col>
                <b-col md="8" class="mt-sm-2">
                    <SmsVerificationBanner @verify="showVerifyPhone" />
                    <div class="hstack py-3">
                        <div class="ms-auto"></div>
                        <a class="" href="#events">
                            <rounded-button class="button-style" :text="'Events'" :onClick="() => events()" :selected="isEvents()" />
                        </a>
                        <a class="" href="#my-events">
                            <rounded-button class="button-style" :text="'My Events'" :onClick="() => myEvents()" :selected="isMyEvents()" />
                        </a>
                        <a class="" href="#past-events">
                            <rounded-button class="button-style" :text="'Past Events'" :onClick="() => pastEvents()" :selected="isPastEvents()" />
                        </a>
                        <a class="" href="#m">
                            <rounded-button class="button-style" :text="'Memories'" :onClick="() => memories()" :selected="isMemories()" />
                        </a>
                        <div class="me-auto"></div>
                    </div>
                    <div v-if="viewModel.displayedEvents.length <= 0 && !viewModel.canLoadMore && !isMemories()">
                        <card-view class="vstack gap-4">
                            <h4>No Events</h4>
                            <span>Your events will show up here. Install the the dateit app to create anevent.</span>
                            <div class="gap-2 hstack">
                                <a href="https://apps.apple.com/us/app/dateit/id1610780514"><img width="168" height="50" class="app-badge" src="/app-store-badge.svg" alt="App Store" /></a>
                                <a href="https://play.google.com/store/apps/details?id=io.dateit.app.android"><img width="168" height="50" class="app-badge" src="/google-play-badge.svg" alt="Google Play" /></a>
                            </div>
                            <div class="hstack">
                                <div>Get SMS notifications and import events</div>
                                <b-button variant="primary" class="ms-auto" @click="showVerifyPhone">Link Phone</b-button>
                            </div>
                        </card-view>
                    </div>
                    <div v-if="!isMemories()">
                        <div v-for="container in viewModel.displayedEvents" :key="container.id" class="">
                            <h5 v-if="container.hasOwnProperty('header')" class="mb-3 ms-2 event-header">
                                {{ container.header }}
                            </h5>
                            <router-link v-if="container.hasOwnProperty('event')" :to="'/e/' + container.event.id">
                                <event-card class="pb-2" :event="container.event"></event-card>
                            </router-link>
                        </div>
                    </div>
                    <div v-else>
                        <div v-for="(group, index) in viewModel.memoriesGroups" :key="index">
                            <router-link :to="'/e/' + group.media[0].eventId" class="event-link">
                                <h5 class="mb-1 ms-2 event-header">{{ group.eventName }}</h5>
                            </router-link>
                            <div class="mb-2 ms-2 event-date">
                                {{ new Date(group.media[0].eventTime).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }) }}
                            </div>
                            <div :id="'memoriesGallery' + index" class="pswp-gallery grid mb-4">
                                <a v-for="media in group.media" :key="media.id" :href="media.photoURL" target="_blank" rel="noreferrer" data-cropped="true" :data-pswp-width="media.width" :data-pswp-height="media.height" :data-pswp-src="media.photoURL">
                                    <media-thumbnail :media="media"></media-thumbnail>
                                </a>
                            </div>
                        </div>
                    </div>
                    <infinite-loading target="#fullScreenPhotoPopup" @infinite="loadMore">
                        <template #spinner>
                            <div class="text-center p-3"><spinner-view v-if="viewModel.canLoadMore" variant="light" /></div
                        ></template>
                    </infinite-loading>
                </b-col>
                <b-col class=""> </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script lang="ts">
import EventCard from "../components/EventCard.vue";
import EventListViewModel, { EventListMode } from "../EventListViewModel";
import { defineComponent, inject, reactive, ref } from "vue";
import SpinnerView from "../components/SpinnerView.vue";
import Api from "@/Api";
import AuthManager from "@/AuthManager";
import VerifyPhone from "@/components/VerifyPhone.vue";
import SmsVerificationBanner from "@/components/SmsVerificationBanner.vue";
import { useToast } from "vue-toastification";
import { mobileUtil } from "@/util/MobileUtil";
import MediaThumbnail from "../components/MediaThumbnail.vue";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";

export default defineComponent({
    name: "EventListView",
    components: {
        EventCard,
        VerifyPhone,
        SmsVerificationBanner,
        SpinnerView,
        MediaThumbnail,
    },
    setup() {
        const api = inject<Api>("api");
        if (!api) {
            throw new Error("API not provided");
        }
        const authManager = inject<AuthManager>("authManager");
        if (!authManager) {
            throw new Error("API not provided");
        }

        const globalRefs = inject("globalRefs");
        if (!globalRefs) {
            throw new Error("API not provided");
        }

        const { isMobile } = mobileUtil();
        const verifyPhonePopup = ref<InstanceType<typeof VerifyPhone> | null>(null);

        return {
            api,
            toast: useToast(),
            authManager,
            viewModel: reactive(new EventListViewModel(api, authManager)),
            globalRefs,
            verifyPhonePopup,
            isMobile,
        };
    },
    async mounted() {
        await this.authManager.waitForAuthState();
        if (!this.viewModel.isLoadingMore) await this.viewModel.initialLoad();

        switch (location.hash) {
            case "#events":
                this.events();
                break;
            case "#my-events":
                this.myEvents();
                break;
            case "#past-events":
                this.pastEvents();
                break;
            case "#m":
                this.memories();
                break;
        }

        if (localStorage.getItem("createEventData") !== null) {
            const newEventData = JSON.parse(localStorage.createEventData);
            // @ts-ignore
            this.globalRefs.createEditEventUi.viewModel.createEventData.fromJson(newEventData);
            // @ts-ignore
            await this.globalRefs.createEditEventUi.onSubmitAfterOnboarding();
            localStorage.removeItem("createEventData");
        }
    },
    methods: {
        async refreshEvents() {
            try { await this.viewModel.initialLoad(); }
            catch { this.toast.error("Events could not be refreshed. Please reload the page."); }
        },
        async refreshVerifiedEvents() {
            try { await this.viewModel.initialLoad(); }
            catch { this.toast.error("Your phone was verified, but events could not be refreshed. Please reload the page."); }
        },
        events() {
            this.viewModel.mode = EventListMode.events;
        },
        myEvents() {
            this.viewModel.mode = EventListMode.myEvents;
        },
        pastEvents() {
            this.viewModel.mode = EventListMode.pastEvents;
        },
        memories() {
            this.viewModel.mode = EventListMode.memories;
        },
        isEvents() {
            return this.viewModel.mode === EventListMode.events;
        },
        isMyEvents() {
            return this.viewModel.mode === EventListMode.myEvents;
        },
        isPastEvents() {
            return this.viewModel.mode === EventListMode.pastEvents;
        },
        isMemories() {
            return this.viewModel.mode === EventListMode.memories;
        },
        showVerifyPhone() {
            this.verifyPhonePopup?.showModal();
        },
        async loadMore() {
            await this.viewModel.loadMore();
        },
        initLightboxes() {
            // Clean up existing lightboxes
            this.lightboxes.forEach((lightbox) => lightbox.destroy());
            this.lightboxes = [];

            // Initialize new lightboxes after a short delay to ensure DOM is updated
            setTimeout(() => {
                this.viewModel.memoriesGroups.forEach((_, index) => {
                    const lightbox = new PhotoSwipeLightbox({
                        gallery: `#memoriesGallery${index}`,
                        children: "a",
                        pswpModule: () => import("photoswipe"),
                    });
                    lightbox.init();
                    this.lightboxes.push(lightbox);
                });
            }, 100);
        },
    },
    data() {
        return {
            lightboxes: [] as PhotoSwipeLightbox[],
        };
    },
    watch: {
        "authManager.accountRevision"() {
            // Discard the old model so its in-flight requests cannot restore another account's events.
            this.viewModel = reactive(new EventListViewModel(this.api, this.authManager));
            if (this.authManager.isLoggedIn) void this.refreshEvents();
            else this.viewModel.isLoaded = true;
        },
        "authManager.isLoggedIn"(loggedIn: boolean) {
            if (loggedIn && !this.viewModel.isLoadingMore) void this.refreshEvents();
        },
        "viewModel.memoriesGroups": {
            handler(newGroups) {
                if (this.isMemories()) {
                    this.initLightboxes();
                }
            },
            deep: true,
        },
    },
    beforeUnmount() {
        this.lightboxes.forEach((lightbox) => lightbox.destroy());
    },
});
</script>

<style scoped>
.button-style {
    font-weight: bold;
    border: none;
    border-radius: 20px;
    margin-bottom: 2px;
}

.event-header {
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
}

.event-date {
    font-size: 0.9rem;
    color: var(--text-secondary);
}

.event-link {
    text-decoration: none;
}

.event-link:hover .event-header {
    text-decoration: underline;
}

@media (max-width: 500px) {
    .button-style {
        min-width: 1px;
    }
}

a {
    text-decoration: none;
    color: var(--text-primary) !important;
}
a:link {
    text-decoration: none;
}
a:visited {
    text-decoration: none;
}
a:hover {
    text-decoration: inherit;
}
a:active {
    text-decoration: inherit;
}
.grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(100px, 1fr));
    gap: 2px;
}
</style>
